/* Copyright (c) 2024-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Directive, ElementRef, Inject, Input, NgZone, OnDestroy, OnInit, Optional } from '@angular/core';
import { Platform, _getEventTarget } from '@angular/cdk/platform';
import { coerceBooleanProperty, coerceElement } from '@angular/cdk/coercion';
import { RippleAnimationConfig } from '@angular/material/core';
import { EventManager } from 'utils/lib/event-manager/event-manager';
import { InputModalityService } from 'input-modality/lib/input-modality.service';
import { NV_HIG_CONFIG, NvHigConfig } from '../hig.config';

interface RippleEventListeners {
    onTransitionEnd?: EventListener;
    onTransitionCancel?: EventListener;
}

interface RippleTarget {
    rippleConfig: RippleConfig;
}

enum FocusAnimStates {
    Off = 0, // no ripple and rect elements
    FocusIn = 1, // during focus in animation
    On = 2, // containerElement is focused and blinking
    FocusOut = 3 // during focusout animation
}

interface AnimationProps {
    rippleRef: ElementRef;
    rectRef: ElementRef;
    targetWidth: number;
    targetHeight: number;
    borderWidth: number;
}

interface RippleConfig {
    color: string;
    borderColor: string;
    animation: RippleAnimationConfig;
}

const focusInEvents = ['focus']; // focusin bubbles, while focus does not
const focusOutEvents = ['blur']; // focusout bubbles, while blur does not
const rippleOverflow = 20; // check hig-button-styling.scss

/** calculate distance from the center of a rectangle to one of its corner */
function calcRadius(rect: { width: number; height: number }) {
    const distX = rect.width / 2;
    const distY = rect.height / 2;
    return Math.sqrt(distX * distX + distY * distY);
}

function setDimensionForAnimatableElems({
    rippleRef,
    rectRef,
    targetWidth: width,
    targetHeight: height,
    borderWidth
}: AnimationProps): void {
    const ripple = rippleRef.nativeElement;
    const rect = rectRef.nativeElement;
    // add rippleOverflow to ensure ripple is big enough to cover the entire button
    const radius = Math.ceil(calcRadius({ width, height })) + rippleOverflow;
    // animate width and height of the ripple and rect elements to ensure there is no clipping artifacts, see Bug 5160280
    ripple.style.height = `${radius * 2}px`;
    ripple.style.width = `${radius * 2}px`;
    rect.style.height = `${Math.ceil(height) - borderWidth * 2}px`;
    rect.style.width = `${Math.ceil(width) - borderWidth * 2}px`;
}

/**
 * Default ripple animation configuration for ripples without an explicit
 * animation config specified.
 */
export const defaultRippleAnimationConfig = {
    enterDuration: 140, // see $focus-ripple-animation-duration in hig-focus-fade-in.scss
    exitDuration: 140
};

export const defaultNvRippleColor = 'var(--hig-focus-ripple-base-color)';

export const defaultNvRippleBorderColor = 'var(--hig-selected-control-color)';

export class FocusRippleRenderer implements EventListenerObject {
    /** Element where the ripples are being added to. */
    private _hostElement: HTMLElement;

    /** Element which triggers the ripple elements on focus/blur events. */
    private _triggerElement: HTMLElement;

    /** The one and only ripple and rect that were triggered. */
    private _currentRippleRef: ElementRef | null;
    private _currentRectRef: ElementRef | null;
    private _currentContainerRef: ElementRef | null;
    private eventListeners: RippleEventListeners = {};

    private static _eventManager = new EventManager();

    private observer: ResizeObserver;

    private focusAnimState = FocusAnimStates.Off;

    constructor(
        private _target: RippleTarget,
        private _ngZone: NgZone,
        elementOrElementRef: HTMLElement | ElementRef<HTMLElement>,
        private _platform: Platform,
        private borderWidth: number,
        private inputModalityService: InputModalityService
    ) {
        // Only do anything if we're on the browser.
        if (_platform.isBrowser) {
            this._hostElement = coerceElement(elementOrElementRef);
        }
    }

    private clearListeners = () => {
        this._currentRippleRef?.nativeElement.removeEventListener('transitionend', this.eventListeners.onTransitionEnd);
        this._currentRippleRef?.nativeElement.removeEventListener(
            'transitioncancel',
            this.eventListeners.onTransitionCancel
        );
    };

    private focusInRipple(config: RippleConfig) {
        this.tearDown();

        // start the animation anew
        this._hostElement.classList.add('nv-focus-ripple-cta-btn');
        this._hostElement.classList.add('focus-green-text');

        const animationConfig = config.animation;
        const borderWidth = this.borderWidth;
        const containerRect = this._hostElement.getBoundingClientRect();

        // adding a container to the host element to ensure that the ripple is not clipped
        // by the host element. This is important for the rect element.
        // The container is removed after the animation is done.
        // The container is also used to calculate the size of the ripple and rect elements.
        // The container is positioned absolutely to ensure that it does not affect the layout of the host element.
        const container = document.createElement('div');
        container.classList.add('nv-ripple-container');

        const ripple = document.createElement('div');
        ripple.classList.add('nv-ripple-circle');

        const rect = document.createElement('div');
        rect.classList.add('nv-ripple-rect');

        // Exposed reference to the ripple that will be returned.
        const rippleRef = new ElementRef(ripple);
        const rectRef = new ElementRef(rect);

        this._hostElement.appendChild(container);
        container.appendChild(ripple);
        container.appendChild(rect);
        this._currentContainerRef = new ElementRef(container);

        // By default the browser does not recalculate the styles of dynamically created
        // ripple elements. This is critical to ensure that the `scale` animates properly.
        // We enforce a style recalculation by calling `getBoundingClientRect`
        // See: https://gist.github.com/paulirish/5d52fb081b3570c81e3a
        this._currentContainerRef.nativeElement.getBoundingClientRect();

        setDimensionForAnimatableElems({
            rippleRef,
            rectRef,
            targetWidth: containerRect.width,
            targetHeight: containerRect.height,
            borderWidth
        });

        ripple.style.backgroundColor = config.color;
        rect.style.backgroundColor = config.color;
        ripple.style.transitionDuration = `${animationConfig.enterDuration}ms`;
        rect.style.transitionDuration = `${animationConfig.enterDuration}ms`;
        ripple.style.opacity = '0.5'; // from 0 to 0.5
        rect.style.opacity = '1'; // from 0.5 to 1

        this.focusAnimState = FocusAnimStates.FocusIn;

        this._currentRippleRef = rippleRef;
        this._currentRectRef = rectRef;

        this._ngZone.runOutsideAngular(() => {
            const onTransitionEnd = e => {
                if (e.propertyName !== 'opacity') {
                    // transitionend fires for each attribute animation
                    // but we only want to run this once
                    // therefore only invoking it for opacity attr
                    return;
                }
                this.clearListeners();
                this.focusAnimState = FocusAnimStates.On;

                this._currentRectRef.nativeElement.style.border = `${borderWidth}px solid ${config.borderColor}`;
                // observe for size change in case this._hostElement changes in size (e.g. due to text change when i18n string comes in late)
                this.observer = new ResizeObserver(
                    ([
                        {
                            borderBoxSize: [{ inlineSize: newWidth, blockSize: newHeight }]
                        }
                    ]) => {
                        if (newWidth !== containerRect.width || newHeight !== containerRect.height) {
                            // update rect and ripple dimensions
                            setDimensionForAnimatableElems({
                                rippleRef: this._currentRippleRef,
                                rectRef: this._currentRectRef,
                                targetWidth: newWidth,
                                targetHeight: newHeight,
                                borderWidth
                            });
                        }
                    }
                );
                this.observer.observe(this._hostElement);
            };
            const onTransitionCancel = e => {
                if (e.propertyName !== 'opacity') {
                    return;
                }
                this.tearDown();
            };
            this.eventListeners = {
                onTransitionCancel,
                onTransitionEnd
            };
            // only tie listeners to ripple
            this._currentRippleRef.nativeElement.addEventListener('transitionend', onTransitionEnd);
            this._currentRippleRef.nativeElement.addEventListener('transitioncancel', onTransitionCancel);
        });
    }

    private tearDown() {
        this.clearListeners();
        this._destroyRipples();
        this.observer?.disconnect();
        this.observer = null;
    }

    private focusOutRipple() {
        this.observer?.disconnect();
        this.clearListeners();
        const ripple = this._currentRippleRef.nativeElement;
        const rect = this._currentRectRef.nativeElement;

        if (!rect || !ripple) {
            return;
        }

        rect.style.border = 'none';

        ripple.style.opacity = '0';
        ripple.style.width = 0;
        ripple.style.height = 0;
        rect.style.opacity = '0.5';
        rect.style.width = 0;
        rect.style.height = 0;
        this._hostElement.classList.remove('focus-green-text');

        this.focusAnimState = FocusAnimStates.FocusOut;

        this._ngZone.runOutsideAngular(() => {
            const domRemoval = e => {
                if (e.propertyName !== 'opacity') {
                    return;
                }
                this.tearDown();
            };
            const onTransitionEnd = e => {
                domRemoval(e);
            };
            const onTransitionCancel = e => {
                domRemoval(e);
            };
            this.eventListeners = {
                onTransitionCancel,
                onTransitionEnd
            };
            // only tie listeners to ripple
            this._currentRippleRef.nativeElement.addEventListener('transitionend', onTransitionEnd);
            this._currentRippleRef.nativeElement.addEventListener('transitioncancel', onTransitionCancel);
        });
    }

    /** Sets up the trigger event listeners */
    setupTriggerEvents(elementOrElementRef: HTMLElement | ElementRef<HTMLElement>) {
        const element = coerceElement(elementOrElementRef);

        if (!this._platform.isBrowser || !element || element === this._triggerElement) {
            return;
        }

        // Remove all previously registered event listeners from the trigger element.
        this.removeTriggerEvents();
        this._triggerElement = element;

        // Use event delegation for the trigger events since they're
        // set up during creation and are performance-sensitive.
        focusInEvents.forEach(type => {
            FocusRippleRenderer._eventManager.addHandler(this._ngZone, type, element, this);
        });

        focusOutEvents.forEach(type => {
            FocusRippleRenderer._eventManager.addHandler(this._ngZone, type, element, this);
        });
    }

    /** Removes previously registered event listeners from the trigger element. */
    removeTriggerEvents() {
        const trigger = this._triggerElement;

        if (trigger) {
            focusInEvents.forEach(type => FocusRippleRenderer._eventManager.removeHandler(type, trigger, this));
            focusOutEvents.forEach(type => FocusRippleRenderer._eventManager.removeHandler(type, trigger, this));
        }
    }

    handleEvent(event: Event) {
        const origin = this.inputModalityService.lastFocusOrigin;
        // if it triggers a blur event and focusAnimState is FocusIn or On, call _onFocusOut regardless of origin
        if (focusOutEvents.includes(event.type)) {
            if (this.focusAnimState === FocusAnimStates.Off || this.focusAnimState === FocusAnimStates.FocusOut) {
                return;
            }
            this._onFocusOut();
        }
        // for focus in events, only trigger when origin is valid
        if (origin !== 'keyboard' && origin !== 'program') {
            return;
        }
        if (focusInEvents.includes(event.type)) {
            if (this.focusAnimState === FocusAnimStates.On || this.focusAnimState === FocusAnimStates.FocusIn) {
                return;
            }
            this._onFocusIn();
        }
    }

    /** Destroys the given ripple by removing it from the DOM */
    private _destroyRipples() {
        this.focusAnimState = FocusAnimStates.Off;
        this._currentContainerRef?.nativeElement?.remove();
        this._currentRippleRef?.nativeElement?.remove();
        this._currentRectRef?.nativeElement?.remove();
        this._currentRippleRef = null;
        this._currentRectRef = null;
        this._hostElement.classList.remove('nv-focus-ripple-cta-btn');
    }

    /** Function being called whenever the trigger is being focused */
    private _onFocusIn() {
        const animationConfig = this._target.rippleConfig.animation;

        if (animationConfig.enterDuration || animationConfig.exitDuration) {
            this.focusInRipple(this._target.rippleConfig);
        }
    }

    /** Function being called whenever the focus is leaving the trigger */
    private _onFocusOut() {
        const animationConfig = this._target.rippleConfig.animation;

        if (animationConfig.enterDuration || animationConfig.exitDuration) {
            this.focusOutRipple();
        }
    }
}

/**
 * Apply this to all hig button if focusRippleEnabled is true
 * note: in targeting button.mat-accent, if the class is being applied dynamically, the directive won't get applied
 * if that case, please rely on applying nvFocusRipple conditionally, e.g. nv-hig-dialog-component
 */
@Directive({
    selector: `
        button[color="accent"],
        button[mat-raised-button],
        button.mat-accent,
        [nvFocusRipple]
    `,
    standalone: true
})
export class NvFocusRippleDirective implements OnInit, OnDestroy, RippleTarget {
    /**
     * Configuration for the ripple animation. Allows modifying the enter and exit animation
     * duration of the ripples.
     */
    @Input('nvRippleAnimation') animation: RippleAnimationConfig = defaultRippleAnimationConfig;
    @Input('nvRippleColor') color: string = defaultNvRippleColor;
    @Input('nvRippleBorderColor') borderColor: string = defaultNvRippleBorderColor;
    /**
     * The element that triggers the ripple when focus events are received.
     * Defaults to the directive's host element.
     */
    @Input('nvRippleTrigger')
    get trigger() {
        return this._trigger || this._elementRef.nativeElement;
    }
    set trigger(trigger: HTMLElement) {
        this._trigger = trigger;
        this._setupTriggerEventsIfEnabled();
    }
    private _trigger: HTMLElement;

    @Input('nvFocusRipple') get flagEnabled() {
        return this._flagEnabled;
    }
    set flagEnabled(value: boolean) {
        // only disable this ripple when it's explicitly false
        this._flagEnabled = coerceBooleanProperty(value);
    }
    private _flagEnabled = true;

    private focusRippleEnabled: boolean;

    /** Renderer for the ripple DOM manipulations. */
    private _rippleRenderer: FocusRippleRenderer;

    private get disableRipple(): boolean {
        return !this.focusRippleEnabled || !this._flagEnabled;
    }

    /** Whether ripple directive is initialized and the input bindings are set. */
    private _isInitialized = false;

    constructor(
        private _elementRef: ElementRef<HTMLElement>,
        ngZone: NgZone,
        platform: Platform,
        inputModalityService: InputModalityService,
        @Optional() @Inject(NV_HIG_CONFIG) config: NvHigConfig
    ) {
        this.focusRippleEnabled = !!config.focusRippleEnabled;
        if (this.disableRipple) {
            return;
        }
        this._rippleRenderer = new FocusRippleRenderer(
            this,
            ngZone,
            _elementRef,
            platform,
            config?.focusRippleBorderWidth,
            inputModalityService
        );
    }

    ngOnInit() {
        this._isInitialized = true;
        this._setupTriggerEventsIfEnabled();
    }

    ngOnDestroy() {
        this._rippleRenderer?.removeTriggerEvents();
    }

    get rippleConfig(): RippleConfig {
        return {
            color: this.color,
            borderColor: this.borderColor,
            animation: this.animation
        };
    }

    /** Sets up the trigger event listeners if ripples are enabled. */
    private _setupTriggerEventsIfEnabled() {
        if (this._isInitialized && !this.disableRipple) {
            this._rippleRenderer.setupTriggerEvents(this.trigger);
        }
    }
}
